import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";

type InlineNode =
  | { type: "text"; value: string }
  | { type: "bold"; children: InlineNode[] }
  | { type: "italic"; children: InlineNode[] };

type ListItem = {
  children: InlineNode[];
  order?: number;
  subList?: { ordered: boolean; items: ListItem[] };
};

type Block =
  | { type: "paragraph"; children: InlineNode[] }
  | { type: "heading"; level: 2 | 3; children: InlineNode[] }
  | { type: "hr" }
  | { type: "list"; ordered: boolean; items: ListItem[]; start?: number }
  | { type: "table"; headers: string[]; rows: string[][] };

/**
 * Sous-ensemble Markdown produit par le chatbot :
 * **gras**, *italique*, titres ##/###, listes, séparateurs ---, tableaux |.
 */

/** Remet des retours ligne quand le LLM aplatit un tableau sur une seule ligne. */
function normalizeCollapsedTables(markdown: string): string {
  return markdown
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => {
      const pipeCount = (line.match(/\|/g) || []).length;
      const hasSep = /\|[\s:-]*-{3,}[\s:-]*\|/.test(line);
      if (!hasSep || pipeCount < 6) {
        return line;
      }
      // `| cell | |---|` ou `---| | next |` → nouvelles lignes
      return line.replace(/\|\s*\|/g, "|\n|");
    })
    .join("\n");
}

function parseInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  const pattern = /(\*\*[^*]+?\*\*|\*[^*]+?\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    const token = match[0];
    if (token.startsWith("**") && token.endsWith("**")) {
      nodes.push({
        type: "bold",
        children: [{ type: "text", value: token.slice(2, -2) }],
      });
    } else if (token.startsWith("*") && token.endsWith("*")) {
      nodes.push({
        type: "italic",
        children: [{ type: "text", value: token.slice(1, -1) }],
      });
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    nodes.push({ type: "text", value: text.slice(lastIndex) });
  }

  return nodes.length ? nodes : [{ type: "text", value: text }];
}

function isTableSeparator(trimmed: string): boolean {
  if (!trimmed.includes("|") || !trimmed.includes("-")) {
    return false;
  }
  // Ligne du type |---|:---| ou ---|---
  const body = trimmed.replace(/^\|/, "").replace(/\|$/, "");
  const cells = body.split("|");
  if (cells.length < 1) {
    return false;
  }
  return cells.every((cell) => /^[\s:-]*-{3,}[\s:-]*$/.test(cell.trim()) || /^[\s:-]+$/.test(cell.trim()));
}

function isTableRow(trimmed: string): boolean {
  if (!trimmed.includes("|")) {
    return false;
  }
  if (isTableSeparator(trimmed)) {
    return false;
  }
  return trimmed.startsWith("|") || /[^|]\|[^|]/.test(trimmed);
}

function parseTableCells(line: string): string[] {
  let t = line.trim();
  if (t.startsWith("|")) {
    t = t.slice(1);
  }
  if (t.endsWith("|")) {
    t = t.slice(0, -1);
  }
  return t.split("|").map((c) => c.trim());
}

function isBlockBoundary(trimmed: string): boolean {
  return (
    /^(-{3,}|\*{3,}|_{3,})$/.test(trimmed) ||
    /^(#{2,3})\s+/.test(trimmed) ||
    isTableRow(trimmed) ||
    isTableSeparator(trimmed)
  );
}

function parseSubBullets(
  lines: string[],
  startIndex: number,
): { subList?: { ordered: boolean; items: ListItem[] }; nextIndex: number } {
  const subItems: ListItem[] = [];
  let i = startIndex;

  while (i < lines.length) {
    if (!lines[i].trim()) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) {
        j += 1;
      }
      if (j >= lines.length) {
        break;
      }
      const next = lines[j].trim();
      if (/^\d+[.)]\s+/.test(next) || isBlockBoundary(next)) {
        break;
      }
      i = j;
      continue;
    }

    const bullet = /^[-*•]\s+(.+)$/.exec(lines[i].trim());
    if (!bullet) {
      break;
    }
    subItems.push({ children: parseInline(bullet[1]) });
    i += 1;
  }

  return {
    subList: subItems.length
      ? { ordered: false, items: subItems }
      : undefined,
    nextIndex: i,
  };
}

function parseListBlock(
  lines: string[],
  startIndex: number,
  ordered: boolean,
): { items: ListItem[]; nextIndex: number } {
  const items: ListItem[] = [];
  let i = startIndex;

  while (i < lines.length) {
    while (i < lines.length && !lines[i].trim()) {
      i += 1;
    }
    if (i >= lines.length) {
      break;
    }

    const current = lines[i].trim();
    if (isBlockBoundary(current) && !/^[-*•]\s+/.test(current) && !/^\d+[.)]\s+/.test(current)) {
      break;
    }

    if (ordered) {
      const numbered = /^(\d+)[.)]\s+(.+)$/.exec(current);
      if (!numbered) {
        break;
      }

      const item: ListItem = {
        children: parseInline(numbered[2]),
        order: Number.parseInt(numbered[1], 10),
      };
      i += 1;

      const { subList, nextIndex } = parseSubBullets(lines, i);
      i = nextIndex;
      if (subList) {
        item.subList = subList;
      }
      items.push(item);
      continue;
    }

    const bullet = /^[-*•]\s+(.+)$/.exec(current);
    if (!bullet) {
      break;
    }
    items.push({ children: parseInline(bullet[1]) });
    i += 1;
  }

  return { items, nextIndex: i };
}

function parseTableBlock(
  lines: string[],
  startIndex: number,
): { block: Block | null; nextIndex: number } {
  let i = startIndex;
  const headerCells = parseTableCells(lines[i].trim());
  i += 1;
  if (i >= lines.length || !isTableSeparator(lines[i].trim())) {
    return { block: null, nextIndex: startIndex };
  }
  i += 1;
  const rows: string[][] = [];
  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (!trimmed) {
      break;
    }
    if (!isTableRow(trimmed)) {
      break;
    }
    const cells = parseTableCells(trimmed);
    // Pad / trim to header width
    const normalized = headerCells.map((_, idx) => cells[idx] ?? "");
    rows.push(normalized);
    i += 1;
  }
  return {
    block: { type: "table", headers: headerCells, rows },
    nextIndex: i,
  };
}

function parseBlocks(markdown: string): Block[] {
  const lines = normalizeCollapsedTables(markdown || "").split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      blocks.push({ type: "hr" });
      i += 1;
      continue;
    }

    const headingMatch = /^(#{2,3})\s+(.+)$/.exec(trimmed);
    if (headingMatch) {
      const level = headingMatch[1].length === 2 ? 2 : 3;
      blocks.push({
        type: "heading",
        level,
        children: parseInline(headingMatch[2].trim()),
      });
      i += 1;
      continue;
    }

    // Tableau : header + ligne séparateur
    if (
      isTableRow(trimmed) &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1].trim())
    ) {
      const { block, nextIndex } = parseTableBlock(lines, i);
      if (block) {
        blocks.push(block);
        i = nextIndex;
        continue;
      }
    }

    const unorderedMatch = /^[-*•]\s+(.+)$/.exec(trimmed);
    const orderedMatch = /^(\d+)[.)]\s+(.+)$/.exec(trimmed);
    if (unorderedMatch || orderedMatch) {
      const ordered = Boolean(orderedMatch);
      const { items, nextIndex } = parseListBlock(lines, i, ordered);
      if (items.length) {
        blocks.push({
          type: "list",
          ordered,
          items,
          start: ordered && items[0]?.order ? items[0].order : undefined,
        });
      }
      i = nextIndex;
      continue;
    }

    const paragraphLines: string[] = [trimmed];
    i += 1;
    while (i < lines.length) {
      const next = lines[i].trim();
      if (!next) break;
      if (/^(-{3,}|\*{3,}|_{3,})$/.test(next)) break;
      if (/^(#{2,3})\s+/.test(next)) break;
      if (/^[-*•]\s+/.test(next) || /^\d+[.)]\s+/.test(next)) break;
      if (
        isTableRow(next) &&
        i + 1 < lines.length &&
        isTableSeparator(lines[i + 1].trim())
      ) {
        break;
      }
      paragraphLines.push(next);
      i += 1;
    }
    blocks.push({
      type: "paragraph",
      children: parseInline(paragraphLines.join(" ")),
    });
  }

  return blocks;
}

function renderInline(nodes: InlineNode[], keyPrefix: string): React.ReactNode[] {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    if (node.type === "text") {
      return <React.Fragment key={key}>{node.value}</React.Fragment>;
    }
    if (node.type === "bold") {
      return (
        <Box component="strong" key={key} sx={{ fontWeight: 700 }}>
          {renderInline(node.children, key)}
        </Box>
      );
    }
    return (
      <Box component="em" key={key} sx={{ fontStyle: "italic" }}>
        {renderInline(node.children, key)}
      </Box>
    );
  });
}

function renderListItems(
  items: ListItem[],
  ordered: boolean,
  keyPrefix: string,
): React.ReactNode {
  const ListTag = ordered ? "ol" : "ul";

  return (
    <Box
      component={ListTag}
      sx={{
        m: 0,
        pl: 2.75,
        listStylePosition: "outside",
        "& > li": {
          pl: 0.5,
          mb: 0.75,
          "&:last-child": { mb: 0 },
        },
        "& > li::marker": {
          color: "text.secondary",
          fontWeight: 500,
        },
        "& li ul, & li ol": {
          mt: 0.5,
          mb: 0,
          pl: 2.25,
        },
      }}
    >
      {items.map((item, itemIndex) => (
        <Typography
          key={`${keyPrefix}-li-${itemIndex}`}
          component="li"
          variant="body2"
          {...(ordered && item.order ? { value: item.order } : {})}
          sx={{
            fontSize: "0.925rem",
            lineHeight: 1.7,
            color: "text.primary",
          }}
        >
          {renderInline(item.children, `${keyPrefix}-li-${itemIndex}`)}
          {item.subList
            ? renderListItems(
                item.subList.items,
                item.subList.ordered,
                `${keyPrefix}-sub-${itemIndex}`,
              )
            : null}
        </Typography>
      ))}
    </Box>
  );
}

function renderTable(block: Extract<Block, { type: "table" }>, key: string) {
  const cellSx = {
    border: "1px solid",
    borderColor: "divider",
    px: 1.25,
    py: 0.75,
    fontSize: "0.85rem",
    lineHeight: 1.45,
    verticalAlign: "top" as const,
    color: "text.primary",
  };

  return (
    <Box
      key={key}
      sx={{
        width: "100%",
        overflowX: "auto",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        component="table"
        sx={{
          borderCollapse: "collapse",
          width: "100%",
          minWidth: 360,
          m: 0,
        }}
      >
        <Box component="thead">
          <Box component="tr">
            {block.headers.map((header, hi) => (
              <Box
                component="th"
                key={`${key}-h-${hi}`}
                sx={{
                  ...cellSx,
                  fontWeight: 650,
                  textAlign: "left",
                  backgroundColor: "action.hover",
                  whiteSpace: "nowrap",
                }}
              >
                {header}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {block.rows.map((row, ri) => (
            <Box component="tr" key={`${key}-r-${ri}`}>
              {row.map((cell, ci) => (
                <Box component="td" key={`${key}-c-${ri}-${ci}`} sx={cellSx}>
                  {cell}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export interface ChatMarkdownProps {
  content: string;
  /** Couleur héritée de la bulle (user vs assistant). */
  color?: string;
}

const ChatMarkdown: React.FC<ChatMarkdownProps> = ({ content, color }) => {
  const blocks = useMemo(() => parseBlocks(content || ""), [content]);

  if (!content.trim()) {
    return null;
  }

  return (
    <Box
      sx={{
        color: color || "inherit",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        "& > *:first-of-type": { mt: 0 },
        "& > *:last-child": { mb: 0 },
      }}
    >
      {blocks.map((block, index) => {
        if (block.type === "hr") {
          return (
            <Box
              key={`hr-${index}`}
              component="hr"
              sx={{
                border: 0,
                borderTop: "1px solid",
                borderColor: "divider",
                width: "100%",
                my: 0.5,
                opacity: 0.45,
              }}
            />
          );
        }

        if (block.type === "heading") {
          return (
            <Typography
              key={`h-${index}`}
              component={block.level === 2 ? "h3" : "h4"}
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: block.level === 2 ? "0.98rem" : "0.92rem",
                letterSpacing: 0.01,
                lineHeight: 1.5,
                mt: index === 0 ? 0 : 0.5,
                mb: 0,
                color: "text.primary",
              }}
            >
              {renderInline(block.children, `h-${index}`)}
            </Typography>
          );
        }

        if (block.type === "table") {
          return renderTable(block, `table-${index}`);
        }

        if (block.type === "list") {
          return (
            <Box
              key={`list-${index}`}
              component={block.ordered ? "ol" : "ul"}
              start={block.ordered ? block.start : undefined}
              sx={{
                m: 0,
                pl: 2.75,
                listStylePosition: "outside",
                "& > li": {
                  pl: 0.5,
                  mb: 1,
                  "&:last-child": { mb: 0 },
                },
                "& > li::marker": {
                  color: "text.secondary",
                  fontWeight: 500,
                },
                "& li ul, & li ol": {
                  mt: 0.5,
                  mb: 0,
                  pl: 2.25,
                },
              }}
            >
              {block.items.map((item, itemIndex) => (
                <Typography
                  key={`li-${index}-${itemIndex}`}
                  component="li"
                  variant="body2"
                  {...(block.ordered && item.order ? { value: item.order } : {})}
                  sx={{
                    fontSize: "0.925rem",
                    lineHeight: 1.7,
                    color: "text.primary",
                  }}
                >
                  {renderInline(item.children, `li-${index}-${itemIndex}`)}
                  {item.subList
                    ? renderListItems(
                        item.subList.items,
                        item.subList.ordered,
                        `li-sub-${index}-${itemIndex}`,
                      )
                    : null}
                </Typography>
              ))}
            </Box>
          );
        }

        return (
          <Typography
            key={`p-${index}`}
            variant="body2"
            sx={{
              m: 0,
              fontSize: "0.925rem",
              lineHeight: 1.7,
              color: "text.primary",
            }}
          >
            {renderInline(block.children, `p-${index}`)}
          </Typography>
        );
      })}
    </Box>
  );
};

export default ChatMarkdown;
